import { createContextFactory } from '@mfw/components';
import { featuresOfRoute } from '@mfw/common';
import React from 'react';

// By module path: the `hooks/tenants` barrel carries every tenant API hook.
import useTenantFeatures from '../hooks/tenants/useTenantFeatures';
import { type UiPermission } from '../schemas/permissions';
import { useAuthContext } from './AuthProvider';

type ContextType = {
  hasPermission: (permission: UiPermission['permission']) => boolean;
  permissions: UiPermission[];
};

const Context = createContextFactory({
  devToolsName: 'PermissionsContext',
  initialValue: {} as ContextType,
});

export interface PermissionsProviderProps {
  children: JSX.Element;
}

const PermissionsProvider = ({ children }: PermissionsProviderProps) => {
  const { currentUser } = useAuthContext();
  const { isOn } = useTenantFeatures();

  // Root CLAUDE.md P5 — a platform user holds absolute rights in scope as a
  // BYPASS, never as a grant list: an empty or lagging `permissions` array
  // must not hide anything. Super-admin-only surfaces keep gating on the
  // explicit `isSuperAdmin` flag, never on this.
  //
  // Per-tenant feature switches: THE frontend chokepoint. A key the shared map
  // ties to a switch (`bookings_create` → `sessions`, `desk_*` → `desk`,
  // `locker_keys_manage` → `lockers`, …) answers false while the acting
  // tenant has that switch off — AFTER the platform bypass, so a platform user
  // keeps every option (owner, Q1), as on the server guard. This is effective
  // UI availability, never a rewrite of the grants: `permissions` below stays
  // exactly what `/auth/me` sent. `featuresOfRoute`, never an index into the
  // map: a key like `toString` would answer with a function.
  const hasPermission = React.useCallback(
    (p: UiPermission['permission']) => {
      if (currentUser?.isPlatformUser) return true;
      if (featuresOfRoute([p], undefined).some((key) => !isOn(key))) {
        return false;
      }
      return Boolean(
        currentUser?.permissions?.some(
          (permission) => permission.permission === p,
        ),
      );
    },
    [currentUser?.isPlatformUser, currentUser?.permissions, isOn],
  );
  const providedValue = React.useMemo(() => {
    return {
      hasPermission,
      permissions: currentUser?.permissions || [],
    };
  }, [currentUser?.permissions, hasPermission]);
  return <Context.Provider value={providedValue}>{children}</Context.Provider>;
};

export function usePermissionsContext() {
  return Context.useContext();
}

export default PermissionsProvider;
